import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  useGetRecentComplaintsQuery,
  useGetActiveComplaintLocationsQuery,
  useGetUserProfileQuery,
  useGetComplaintByIdQuery,
  useCreateComplaintRatingMutation,
} from '../../redux/complaintApi';
import { useAuth } from '../../context/AuthContext';
import { ComplaintStarRating } from '../ComplaintStarRating';
import { ComplaintDetailReadView } from './ComplaintDetailReadView';
import { ComplaintTrackingModal } from './ComplaintTrackingModal';
import {
  complaintRatingFromRaw,
  complaintStatusAllowsEmployeeRating,
} from '../../utils/complaintRating';
import { getComplaintStatusBadgeClass } from '../../utils/complaintStatus';
import {
  ADMIN_COMPLAINT_TABLE_HEADERS,
  mapAdminStyleComplaintRows,
} from './backendAssignedComplaintRows';

const getStatusClasses = (status) => getComplaintStatusBadgeClass(status);

function ComplaintsTable({ showRaisedBy = true, showViewAll = false, viewAllPath = '' }) {
  const { user, area } = useAuth();
  const isAdmin = area === 'admin';
  const isEmployeeArea = area === 'employee';
  const loggedInEmployeeId = user?.employeeCode || '';
  const [selectedRow, setSelectedRow] = useState(null);
  const [submittedDetailRating, setSubmittedDetailRating] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isTrackingOpen, setIsTrackingOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [createComplaintRating, { isLoading: isSavingComplaintRating }] =
    useCreateComplaintRatingMutation();

  const { data: recentRes, isLoading, isError } = useGetRecentComplaintsQuery();
  const { data: locationsRes } = useGetActiveComplaintLocationsQuery();
  useGetUserProfileQuery(loggedInEmployeeId, {
    skip: !loggedInEmployeeId || isAdmin,
  });

  const rows = useMemo(() => {
    const list = recentRes?.data ?? recentRes?.data?.data ?? recentRes;
    if (!Array.isArray(list)) return [];
    return mapAdminStyleComplaintRows(list, locationsRes?.data || []);
  }, [recentRes, locationsRes?.data]);

  const detailComplaintId = selectedRow?.raw?.id ?? selectedRow?.numericId;
  const {
    data: complaintDetailRes,
    isFetching: isDetailFetching,
    isError: isDetailError,
  } = useGetComplaintByIdQuery(detailComplaintId, {
    skip: detailComplaintId == null || detailComplaintId === '' || !selectedRow,
  });

  const viewRaw = useMemo(() => {
    if (!selectedRow?.raw) return null;
    const pkg = complaintDetailRes?.data ?? complaintDetailRes;
    const nested = pkg?.data?.data ?? pkg?.data ?? pkg;
    const rawDetail =
      nested && typeof nested === 'object' && !Array.isArray(nested) ? nested : null;
    return rawDetail || selectedRow.raw;
  }, [selectedRow, complaintDetailRes]);

  const isViewItsm = String(viewRaw?.complaint_type || '').toUpperCase() === 'ITSM';

  const canEmployeeSubmitReview = useMemo(() => {
    if (!isEmployeeArea || !viewRaw) return false;
    if (!complaintStatusAllowsEmployeeRating(viewRaw)) return false;
    const existing = complaintRatingFromRaw(viewRaw);
    if (existing?.rating >= 1) return false;
    if (submittedDetailRating?.rating >= 1) return false;
    return true;
  }, [isEmployeeArea, viewRaw, submittedDetailRating]);

  useEffect(() => {
    if (!selectedRow) return;
    const fromApi = complaintRatingFromRaw(viewRaw);
    if (fromApi?.rating >= 1) setSubmittedDetailRating(null);
  }, [selectedRow, viewRaw]);

  const resetReviewForm = () => {
    setIsReviewModalOpen(false);
    setReviewRating(0);
    setReviewFeedback('');
  };

  const closeModal = () => {
    setSelectedRow(null);
    setSubmittedDetailRating(null);
    setIsTrackingOpen(false);
    resetReviewForm();
  };

  const handleOpenReviewModal = () => {
    setReviewRating(0);
    setReviewFeedback('');
    setIsReviewModalOpen(true);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!selectedRow) return;
    if (reviewRating < 1 || reviewRating > 5) {
      toast.error('Please select a star rating (1–5).');
      return;
    }
    const complaintId = selectedRow.raw?.id ?? selectedRow.numericId ?? selectedRow.id;
    if (complaintId == null || String(complaintId).trim() === '') {
      toast.error('Missing complaint id.');
      return;
    }
    try {
      await createComplaintRating({
        complaint_id: complaintId,
        rating: reviewRating,
        feedback: String(reviewFeedback || '').trim(),
      }).unwrap();
      setSubmittedDetailRating({
        rating: reviewRating,
        feedback: String(reviewFeedback || '').trim(),
      });
      resetReviewForm();
      toast.success('Review submitted.');
    } catch (err) {
      const msg =
        err?.data?.message || err?.message || 'Could not submit review. Please try again.';
      toast.error(msg);
    }
  };

  const openRowDetail = (row) => {
    setSubmittedDetailRating(null);
    setIsReviewModalOpen(false);
    setReviewRating(0);
    setReviewFeedback('');
    setSelectedRow(row);
  };

  return (
    <div className="rounded-xl bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-base font-semibold text-gray-900">Recent Complaints</h3>
        {showViewAll && viewAllPath ? (
          <Link
            to={viewAllPath}
            className="inline-flex w-full items-center justify-center rounded-md bg-brand px-4 py-2 text-xs font-semibold text-white shadow-sm sm:w-auto"
          >
            View All
          </Link>
        ) : null}
      </div>
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading recent complaints…</p>
      ) : isError ? (
        <p className="text-sm text-red-600">Could not load recent complaints.</p>
      ) : rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500">No recent complaints.</p>
      ) : (
        <>
          <ul className="mb-4 space-y-3 md:hidden">
            {rows.map((row, index) => (
              <li
                key={row.numericId ?? row.id}
                className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-gray-500">
                      #{index + 1} · {row.complaintType}
                    </p>
                    <p className="mt-1 truncate text-sm font-semibold text-gray-900">
                      {row.location}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => openRowDetail(row)}
                    className="shrink-0 rounded-lg p-1.5 text-red-700 hover:bg-white"
                    aria-label="View complaint"
                    title="View"
                  >
                    <FaEye className="h-5 w-5" />
                  </button>
                </div>
                <div className="mt-2 space-y-1 text-xs text-gray-600">
                  <p>
                    Complaint ID:{' '}
                    <span className="font-medium text-gray-800">{row.acknowledgementNumber}</span>
                  </p>
                  {showRaisedBy ? (
                    <p>
                      Raised By:{' '}
                      <span className="font-medium text-gray-800">{row.raisedBy}</span>
                    </p>
                  ) : null}
                  <p>
                    Manager:{' '}
                    <span className="font-medium text-gray-800">{row.managerOnDuty}</span>
                  </p>
                  <p className="text-[11px] text-gray-500">{row.reportedDate}</p>
                  <p>
                    TAT:{' '}
                    <span className="font-medium text-gray-800">{row.tat}</span>
                  </p>
                </div>
                <div className="mt-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${getStatusClasses(
                      row.status,
                    )}`}
                  >
                    {row.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full border-collapse text-xs sm:text-sm">
              <thead>
                <tr>
                  {ADMIN_COMPLAINT_TABLE_HEADERS.map((h) => (
                    <th
                      key={h}
                      className="whitespace-nowrap border-b border-gray-200 px-3 py-2 text-left font-semibold text-gray-600"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.numericId ?? row.id} className="hover:bg-amber-50/40">
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-3 text-gray-700">
                      {index + 1}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-3 text-gray-700">
                      {row.acknowledgementNumber}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-3 text-gray-700">
                      {row.complaintType}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-3 text-gray-700">
                      {row.location}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-3 text-gray-700">
                      {row.raisedBy}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-3 text-gray-700">
                      {row.managerOnDuty}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-3 text-gray-700">
                      {row.reportedDate}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-3 text-gray-700">
                      {row.tat}
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusClasses(
                          row.status,
                        )}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-3">
                      <button
                        type="button"
                        onClick={() => openRowDetail(row)}
                        className="text-red-700 hover:text-red-800"
                        aria-label="View complaint"
                        title="View"
                      >
                        <FaEye className="h-5 w-5 text-red-700" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selectedRow && viewRaw ? (
        <div className="fixed inset-0 z-30 flex justify-end bg-black/30">
          <div className="flex h-full w-full max-w-xl flex-col gap-4 overflow-y-auto bg-white p-4 shadow-2xl sm:p-6">
            {isDetailFetching ? (
              <p className="text-xs text-gray-500">Loading latest complaint details…</p>
            ) : null}
            {isDetailError ? (
              <p className="text-xs text-amber-700">
                Could not load full complaint from server — showing row data only.
              </p>
            ) : null}
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-base font-semibold text-gray-900">
                  {isViewItsm ? 'ITSM Complaint Information' : 'CTM Complaint Information'}
                </h2>
                <span className="inline-flex items-center rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white">
                  {viewRaw?.work_priority_name ?? 'N/A'}
                </span>
                {canEmployeeSubmitReview ? (
                  <button
                    type="button"
                    onClick={handleOpenReviewModal}
                    className="inline-flex items-center rounded-full border border-brand bg-white px-3 py-1 text-xs font-semibold text-brand shadow-sm hover:bg-[#faf8f5]"
                  >
                    Add Review +
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                className="text-3xl text-red-700 hover:text-red-800"
                onClick={closeModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <ComplaintDetailReadView
              raw={
                submittedDetailRating?.rating >= 1
                  ? { ...viewRaw, rating_details: submittedDetailRating }
                  : viewRaw
              }
              assignedToLabel={viewRaw?.assigned_to_name}
              onTrackStatusClick={
                isEmployeeArea ? () => setIsTrackingOpen(true) : undefined
              }
            />
          </div>
        </div>
      ) : null}

      {isEmployeeArea ? (
        <ComplaintTrackingModal
          complaintId={viewRaw?.id ?? selectedRow?.numericId ?? selectedRow?.id}
          open={isTrackingOpen}
          onClose={() => setIsTrackingOpen(false)}
        />
      ) : null}

      {isReviewModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Complaint review"
          onClick={() => setIsReviewModalOpen(false)}
        >
          <form
            className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl sm:p-6"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleReviewSubmit}
          >
            <h3 className="text-lg font-semibold text-brand">Add Review and Rating</h3>
            <div className="mt-4">
              <span className="mb-2 block text-sm font-medium text-gray-700">Rating</span>
              <ComplaintStarRating value={reviewRating} onChange={setReviewRating} />
            </div>
            <label className="mt-4 block">
              <span className="mb-1 block text-sm font-medium text-gray-700">Feedback</span>
              <textarea
                value={reviewFeedback}
                onChange={(e) => setReviewFeedback(e.target.value)}
                rows={4}
                placeholder="Share your experience…"
                className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm text-gray-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsReviewModalOpen(false)}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSavingComplaintRating || reviewRating < 1}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {isSavingComplaintRating ? 'Submitting…' : 'Submit review'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}

export default ComplaintsTable;
